<template>
  <!-- Powered By ShareInvestor Thailand -->
  <NuxtLayout>
    <NuxtLoadingBar :duration="1000" />
    <NuxtPage />
  </NuxtLayout>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useTranslationStore } from '@/stores/translations';

// Awaited at the app root so translations are loaded before any page/layout
// renders (both SSR and client) — components further down call
// fetchTranslations() too via unawaited callOnce(), which raced with their
// own render and could show raw translation keys instead of text.
const translationStore = useTranslationStore();
await translationStore.fetchTranslations();

const route = useRoute();
const { public: { siteUrl, meta } } = useRuntimeConfig();

// i18n uses prefix strategy (en/th), so the real homepage route is /en/home
// or /th/home — "/" and "/home" just redirect into one of those via
// pages/index.vue. Organization/WebSite JSON-LD only needs one @id per site,
// so it's gated here rather than duplicated on every route.
const isHomePage = computed(() => route.path === '/' || route.path.endsWith('/home'));

// siteUrl comes from runtimeConfig (appBase), not useRequestURL() — routeRules
// prerenders several pages at build time, and Nitro's prerender crawl origin
// defaults to localhost, which would get baked into the static HTML.
useHead(() => ({
  link: [
    {
      rel: 'canonical',
      href: `${siteUrl}${route.path}`,
    },
  ],
  script: isHomePage.value
    ? [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': `${siteUrl}/#organization`,
                name: meta.title,
                url: `${siteUrl}/`,
                logo: `${siteUrl}/static/images/default/logo-si.svg`,
                description:
                  'ShareInvestor Thailand ให้บริการด้านนักลงทุนสัมพันธ์ออนไลน์และการออกแบบเว็บไซต์สำหรับบริษัทในประเทศไทย รวมถึงบริการด้านการตลาดดิจิทัลและความปลอดภัยทางไซเบอร์',
                // trims Facebook's ?ref=br_rs referral tracking param — the
                // rest come from the same source Footer.vue's social icons use
                sameAs: [
                  translationStore.translate('facebook.url').split('?')[0],
                  translationStore.translate('twitter.url'),
                  translationStore.translate('instagram.url'),
                  translationStore.translate('linkedin.url'),
                  translationStore.translate('youtube.url'),
                ],
              },
              {
                '@type': 'WebSite',
                '@id': `${siteUrl}/#website`,
                url: `${siteUrl}/`,
                name: meta.title,
                publisher: { '@id': `${siteUrl}/#organization` },
                inLanguage: ['en', 'th'],
              },
            ],
          }),
        },
      ]
    : [],
}));
</script>

<style>
/* ... styles ... */
</style>
